import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  ChevronDown, Building, Users, Shield, PieChart, 
  TrendingUp, FolderOpen, FileSearch, ClipboardCheck, Gift, Rocket 
} from "lucide-react";

const chapters = [
  { num: "01", title: "Choosing Your Business Entity", desc: "Pvt Ltd vs LLP vs OPC and exactly why this first decision determines whether investors will ever sit across the table from you.", icon: Building },
  { num: "02", title: "The Co-Founder Agreement", desc: "Equity splits, 4 year vesting, IP assignment, deadlock resolution. The document most founders skip and regret in month 8.", icon: Users },
  { num: "03", title: "Protecting Your IP", desc: "Trademarks, patents, copyright on code, trade secrets. Your moat is only a moat if it legally belongs to the company.", icon: Shield },
  { num: "04", title: "Building Your Cap Table", desc: "Fully diluted ownership math, ESOP pools, face value vs issue price and the pre-money ESOP shuffle investors use to quietly dilute founders.", icon: PieChart },
  { num: "05", title: "Fundraising: Valuation & Instruments", desc: "Pre money vs post money, CCPS, CCD, SAFE, anti dilution, liquidation preference every term between you and your first cheque.", icon: TrendingUp },
  { num: "06", title: "The Data Room", desc: "What goes in it, how to structure it, and why a clean data room can close a round weeks faster than a messy one kills it.", icon: FolderOpen },
  { num: "07", title: "Term Sheets & Due Diligence", desc: "The SSA, SHA, drag-along, tag-along, reserved matters what investors actually verify and what kills deals in the final hour.", icon: FileSearch },
  { num: "08", title: "Governance Post-Investment", desc: "MCA filings, board structure, compliance calendar, investor reporting. The reality that starts the morning after the money lands.", icon: ClipboardCheck },
  { num: "09", title: "ESOPs & Tax", desc: "How to hire great people without competing on salary. The two tax events, DPIIT deferral, and the most common ESOP mistakes.", icon: Gift },
  { num: "10", title: "Future Rounds, Exits & Liquidity", desc: "Series A dilution math, secondary sales, IPO vs acquisition and when it finally makes sense to take money off the table.", icon: Rocket },
];

const ChaptersSection = () => {
  const [openIndex, setOpenIndex] = useState(0);

  return (
    <>
      <style>{`
        .cs-section {
          /* Locked at exactly 80px top and bottom */
          padding-top: 80px;
          padding-bottom: 80px;
          padding-left: 1rem;
          padding-right: 1rem;
          background-color: #0b1120; /* Dark theme to match founders */
          overflow: hidden;
          width: 100%;
          box-sizing: border-box;
        }

        .cs-container {
          max-width: 64rem; /* Widened slightly for 2 columns */
          margin: 0 auto;
        }

        .cs-header {
          text-align: center;
          margin-bottom: 3.5rem;
        }

        .cs-label {
          font-size: 0.75rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.15em;
          color: #2dd4bf;
          margin-bottom: 1rem;
          display: block;
        }

        .cs-title {
          font-size: 1.75rem;
          font-weight: 800;
          line-height: 1.2;
          margin-bottom: 1rem;
          color: #f8fafc;
        }

        .cs-gradient {
          background: linear-gradient(to right, #2dd4bf, #22d3ee);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }

        .cs-description {
          color: #94a3b8;
          font-size: 0.95rem;
          line-height: 1.6;
          max-width: 42rem;
          margin: 0 auto;
        }

        .cs-list {
          display: grid;
          grid-template-columns: 1fr; /* 1 col for mobile */
          gap: 1rem;
          align-items: start;
        }

        .cs-item {
          border-radius: 1rem;
          border: 1px solid rgba(255, 255, 255, 0.08);
          background: rgba(255, 255, 255, 0.03);
          transition: all 0.3s ease;
          cursor: pointer;
          overflow: hidden;
          height: fit-content;
        }

        .cs-item:hover {
          border-color: rgba(45, 212, 191, 0.3);
          background: rgba(255, 255, 255, 0.05);
        }

        .cs-item-active {
          background: rgba(255, 255, 255, 0.06);
          border-color: rgba(45, 212, 191, 0.4);
          box-shadow: 0 10px 30px -10px rgba(0, 0, 0, 0.5);
        }

        .cs-item-header {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 1.25rem;
        }

        .cs-icon-box {
          width: 2.75rem;
          height: 2.75rem;
          border-radius: 0.75rem;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          background: rgba(45, 212, 191, 0.1);
          color: #2dd4bf;
        }

        .cs-icon-active {
          background: #2dd4bf;
          color: #0b1120;
        }

        .cs-ch-num {
          font-size: 10px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          color: rgba(45, 212, 191, 0.6);
        }

        .cs-ch-title {
          font-weight: 700;
          font-size: 0.9rem;
          margin: 0;
          color: #f1f5f9;
        }

        .cs-title-active {
          color: #2dd4bf;
        }

        .cs-content-text {
          font-size: 0.875rem;
          color: #94a3b8;
          line-height: 1.6;
          margin: 0;
        }

        @media (min-width: 768px) {
          .cs-list { 
            grid-template-columns: 1fr 1fr; /* 2 cols for Tab & Desktop */
            gap: 1.5rem; 
          }
          .cs-title { font-size: 2.5rem; }
          .cs-item-header { padding: 1.5rem; }
          .cs-ch-title { font-size: 1rem; }
        }
      `}</style>

      <section className="cs-section">
        <div className="cs-container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="cs-header"
          >
            <span className="cs-label">What's Inside</span>
            <h2 className="cs-title">
              10 decisions every Indian{" "}
              <span className="cs-gradient">founder faces.</span>
            </h2>
            <p className="cs-description">
              Each chapter is a real moment in Ayaan and Radha's journey. Each one ends with a checklist you can act on immediately.
            </p>
          </motion.div>

          <div className="cs-list">
            {chapters.map((ch, i) => {
              const isOpen = openIndex === i;
              const Icon = ch.icon;
              return (
                <motion.div
                  key={ch.num}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                >
                  <div
                    className={`cs-item ${isOpen ? "cs-item-active" : ""}`}
                    onClick={() => setOpenIndex(isOpen ? null : i)}
                  >
                    <div className="cs-item-header">
                      <motion.div
                        className={`cs-icon-box ${isOpen ? "cs-icon-active" : ""}`}
                        animate={isOpen ? { rotate: [0, -5, 0] } : {}}
                      >
                        <Icon size={20} />
                      </motion.div>
                      
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <span className="cs-ch-num">Ch {ch.num}</span>
                        <h3 className={`cs-ch-title ${isOpen ? "cs-title-active" : ""}`}>
                          {ch.title}
                        </h3>
                      </div>

                      <motion.div
                        animate={{ rotate: isOpen ? 180 : 0 }}
                        transition={{ duration: 0.3 }}
                      >
                        <ChevronDown size={20} color="#64748b" />
                      </motion.div>
                    </div>

                    <AnimatePresence initial={false}>
                      {isOpen && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3, ease: "easeInOut" }}
                          style={{ overflow: "hidden" }}
                        >
                          <div style={{ padding: "0 1.5rem 1.5rem 1.5rem" }}>
                            <p className="cs-content-text">{ch.desc}</p>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>
    </>
  );
};

export default ChaptersSection;